// Do Not Disturb with an expiry (shared/API.md §41.7): the «Personalizado…» end time and the
// «hasta HH:mm» line the status menu shows while DND is on. The fixed choices (30 min, 1 h, 2 h,
// «Mañana a las 9:00») live in ./accountSettings.ts `dndEndTime`. `dnd_end_time` is Unix SECONDS on
// the wire; 0 means "until turned off".
import { civil, isSameDay } from './time';
import { addingDays, epochForCivil } from './zonedTime';

export type DndCustomProblem = 'invalid' | 'past';

/**
 * The end time for a «Personalizado…» pick: `date` = `YYYY-MM-DD`, `time` = `HH:mm` on the reader's
 * clock (`timeZone`). A malformed value is `invalid`; a moment not after `now` is `past` (the server
 * would accept it and DND would end at once — the webapp refuses it too).
 */
export function dndCustomEndTime(date: string, time: string, now: number, timeZone?: string): { seconds: number } | { problem: DndCustomProblem } {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim());
  const tm = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!d || !tm) return { problem: 'invalid' };
  const [year, month, day, hour, minute] = [Number(d[1]), Number(d[2]), Number(d[3]), Number(tm[1]), Number(tm[2])];
  if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59) return { problem: 'invalid' };
  const ms = epochForCivil(year, month, day, hour, minute, 0, timeZone);
  // A day that does not exist in that month (31 de febrero) rolls over: refuse it rather than guess.
  const back = civil(ms, timeZone);
  if (back.day !== day || back.month !== month) return { problem: 'invalid' };
  if (ms <= now) return { problem: 'past' };
  return { seconds: Math.floor(ms / 1000) };
}

/** The form's starting values: one hour from now, rounded up to the next quarter hour. */
export function dndCustomDefaults(now: number, timeZone?: string): { date: string; time: string } {
  const quarter = 15 * 60_000;
  const start = Math.ceil((now + 60 * 60_000) / quarter) * quarter;
  const c = civil(start, timeZone);
  const pad = (n: number) => String(n).padStart(2, '0');
  return { date: `${c.year}-${pad(c.month)}-${pad(c.day)}`, time: `${pad(c.hour)}:${pad(c.minute)}` };
}

export type DndUntil =
  | { kind: 'none' }
  | { kind: 'today'; time: string }
  | { kind: 'tomorrow'; time: string }
  | { kind: 'later'; date: string; time: string };

/**
 * What «No molestar» says about its end: nothing (no end / already over), «hasta 14:30» today,
 * «hasta mañana 9:00» tomorrow, «hasta dom 19 oct 9:00» beyond. Times follow the reader's clock
 * style (`hour12`).
 */
export function dndUntil(endSeconds: number, now: number, locale: string, timeZone?: string, hour12?: boolean): DndUntil {
  if (!endSeconds || endSeconds * 1000 <= now) return { kind: 'none' };
  const ms = endSeconds * 1000;
  const timeOptions: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', timeZone };
  if (hour12 !== undefined) timeOptions.hour12 = hour12;
  const time = new Intl.DateTimeFormat(locale, timeOptions).format(ms);
  if (isSameDay(ms, now, timeZone)) return { kind: 'today', time };
  if (isSameDay(ms, addingDays(now, 1, timeZone), timeZone)) return { kind: 'tomorrow', time };
  const date = new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short', timeZone }).format(ms);
  return { kind: 'later', date, time };
}
